import { Link } from "react-router-dom";
import { useEffect, useState } from "react";
import axios from "axios";
import { Button } from "../ui/button";
import { Card, CardContent } from "../ui/card";
import { CheckCircle, CreditCard, Smartphone, Building2, Wallet } from "lucide-react";
import { COUNTRIES } from "../../utils/countries";
import { useCMSContent } from "../../hooks/useCMSContent";
import { useI18n } from "../../i18n";
import { usePricing } from "../../context/PricingContext";
import { countryToLang } from "../../i18n";

const METHOD_ICONS = {
  Yape: Smartphone,
  Plin: Smartphone,
  Bank: Building2,
  MercadoPago: Wallet,
  Card: CreditCard,
};

const PricingCard = () => {
  // iter72 FIX A/B — contexto global: país + plan compartidos por TODA la app
  const {
    country, setCountry, planMonths, setPlanMonths, display,
    priceLabelSemantic, isApproxDisplay, chargeNote, approxHint,
    estimateConfigured, noEstimateNote, chargeLabel,
  } = usePricing();
  const { setLangSoft } = useI18n();
  const { data: cms } = useCMSContent("landing_json");
  const { t } = useI18n();
  const p = cms?.pricing || {};



  // iter64/iter72 — big amount changes by country AND selected plan
  // 4-D1 regla 4: sin fallbacks numéricos — sin datos, skeleton "…"
  // RC-WEB-3 §4 — sin estimación local configurada se pinta el cobro real (PEN),
  // nunca el importe del fallback internacional con el símbolo del país.
  const selPlan = display?.plans?.[String(planMonths)];
  const bigAmount = selPlan
    ? Number(estimateConfigured ? selPlan.amount : selPlan.pen).toLocaleString("es")
    : "…";
  const bigCurrency = display
    ? (estimateConfigured ? (display.symbol || display.currency) : (display.pen_symbol || "S/"))
    : "";
  const savingsLocal = estimateConfigured ? display?.savings?.local : display?.savings?.pen;
  const savingsCur = bigCurrency;

  return (
    <section id="precio" className="bg-[#F8FAFC] py-20 md:py-28" data-testid="pricing-section">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="mb-12 text-center">
          <p className="mb-3 text-xs font-bold uppercase tracking-[0.2em] text-[#1B84FF]">{t("pricing.eyebrow")}</p>
          <h2 className="text-3xl font-extrabold tracking-tight text-[#0B2545] sm:text-4xl lg:text-5xl">
            {t("pricing.section_title")}
          </h2>
        </div>
        <div className="mx-auto max-w-lg">
          <Card className="overflow-hidden rounded-3xl border border-slate-200/80 shadow-[0_20px_60px_rgba(11,37,69,0.12)]">
            <div className="relative overflow-hidden bg-[#0B2545] p-8 text-center">
              <div
                className="pointer-events-none absolute -right-16 -top-16 h-56 w-56 rounded-full opacity-40 blur-3xl"
                style={{ background: "radial-gradient(circle, #1B84FF 0%, rgba(11,37,69,0) 70%)" }}
                aria-hidden="true"
              />
              <div className="relative">
              <h3 className="text-white text-2xl font-bold mb-2" data-testid="pricing-card-title">
                {p.card_title || "Membresía AvisoCar"}
              </h3>
              <div className="flex items-baseline justify-center gap-1">
                {isApproxDisplay && <span className="text-white/70 text-2xl" aria-hidden="true">≈</span>}
                <span className="text-white/80 text-xl" data-testid="pricing-big-currency">{bigCurrency}</span>
                <span className="text-white text-6xl font-bold" data-testid="pricing-big-amount">{bigAmount}</span>
                {isApproxDisplay && (
                  <span className="text-white/80 text-sm font-semibold uppercase tracking-wide" data-testid="pricing-approx-tag">
                    {t("pricing.aprox")}
                  </span>
                )}
              </div>
              <p className="text-white/90 mt-2 font-medium" data-testid="pricing-period">
                {planMonths === 12 ? t("pricing.per_12m") : t("pricing.per_6m")}
              </p>

              {/* iter72 corte 4.1 — selector 6 meses / 1 año */}
              <div className="mt-4 grid grid-cols-2 gap-2" data-testid="pricing-plan-toggle">
                {[6, 12].map((m) => {
                  const pl = display?.plans?.[String(m)];
                  const amount = pl
                    ? Number(estimateConfigured ? pl.amount : pl.pen).toLocaleString("es")
                    : "…";
                  const selected = planMonths === m;
                  return (
                    <button
                      key={m}
                      type="button"
                      onClick={() => setPlanMonths(m)}
                      className={`relative rounded-xl px-2 pt-3 pb-2 text-sm font-bold transition-colors border-2 ${
                        selected ? "bg-white text-[#0B2545] border-white" : "bg-white/10 text-white border-white/25 hover:bg-white/20"
                      }`}
                      data-testid={`pricing-plan-${m}`}
                    >
                      {m === 12 && (
                        <span className="absolute -top-2 left-1/2 -translate-x-1/2 px-2 py-0.5 rounded-full text-[9px] font-bold text-[#0B2545] whitespace-nowrap bg-[#5BC0FF]">
                          ★ {t("pricing.best_value")}
                        </span>
                      )}
                      {m === 6 ? t("pricing.plan_6m") : t("pricing.plan_12m")}
                      <span className="block text-xs font-semibold opacity-80">
                        {isApproxDisplay ? "≈ " : ""}{bigCurrency} {amount}
                      </span>
                    </button>
                  );
                })}
              </div>
              {planMonths === 12 && savingsLocal > 0 && (
                <p className="mt-2 text-[12px] font-bold text-white bg-white/15 rounded-full px-3 py-1 inline-block" data-testid="pricing-savings">
                  {t("pricing.savings", { currency: savingsCur, amount: Number(savingsLocal).toLocaleString("es") })}
                </p>
              )}

              <div className="mt-4 bg-white/15 rounded-xl p-3 text-left" data-testid="pricing-intl-box">
                <label className="text-white/80 text-xs block mb-1">{p.intl_country_label || "Tu país"}</label>
                <select
                  value={country || "XX"}
                  onChange={(e) => { setCountry(e.target.value); const l = countryToLang(e.target.value); if (l) setLangSoft(l); }}
                  className="w-full h-9 rounded-lg bg-white/90 text-slate-800 text-sm px-2 outline-none"
                  data-testid="pricing-country-select"
                >
                  <option value="XX">🌐 {t("pricing.intl_other")}</option>
                  {COUNTRIES.map((c) => (
                    <option key={c.iso} value={c.iso}>{c.flag} {c.name}</option>
                  ))}
                </select>
                {!display?.own_price && chargeNote(planMonths) && (
                  <p className="text-white font-semibold mt-2 text-sm" data-testid="pricing-approx">
                    {chargeNote(planMonths)}
                  </p>
                )}
                {!display?.own_price && approxHint() && (
                  <p className="text-white/80 text-[11px] mt-1" data-testid="pricing-approx-hint">
                    {approxHint()}
                  </p>
                )}
                {display?.own_price && (
                  <p className="text-white/80 text-[11px] mt-1" data-testid="pricing-note">
                    {t("pricing.own_price_note")}
                  </p>
                )}
                {noEstimateNote() && (
                  <p className="text-white/85 text-[11px] mt-1" data-testid="pricing-no-estimate">
                    {noEstimateNote()}
                  </p>
                )}
              </div>
              </div>
            </div>
            <CardContent className="p-8">
              <ul className="space-y-4 mb-6" data-testid="pricing-features">
                {(p.features || []).map((f, i) => (
                  <li key={i} className="flex items-center gap-3" data-testid={`pricing-feature-${i}`}>
                    <CheckCircle className="w-5 h-5 text-[#1B84FF] flex-shrink-0" />
                    <span className="text-slate-700">{f}</span>
                  </li>
                ))}
              </ul>

              {(p.payment_methods_pe || p.payment_methods_intl) && (
                <div className="mb-6 border-t pt-5" data-testid="pricing-methods">
                  <p className="text-xs uppercase tracking-wide text-slate-500 font-semibold mb-3">
                    {p.payment_methods_title || "Métodos de pago"}
                  </p>
                  {display?.country === "IN" ? (
                    <div data-testid="pricing-methods-in">
                      <div className="flex flex-wrap gap-2">
                        <div className="flex items-center gap-1.5 bg-slate-100 px-3 py-1.5 rounded-full text-xs text-slate-700" data-testid="pricing-method-upi">
                          <CreditCard className="w-3.5 h-3.5 text-amber-600" />
                          UPI (GPay · PhonePe · Paytm)
                        </div>
                      </div>
                      <p className="text-[11px] text-slate-500 mt-2">{t("checkout.upi_honest")}</p>
                    </div>
                  ) : (
                  <>
                  {(!display || display.country === "PE") && p.payment_methods_pe?.length > 0 && (
                    <div className="mb-3" data-testid="pricing-methods-pe">
                      <p className="text-[11px] font-semibold text-slate-600 mb-1.5">{p.payment_methods_pe_label || "Si estás en Perú"}</p>
                      <div className="flex flex-wrap gap-2">
                        {p.payment_methods_pe.map((m, i) => {
                          const Icon = METHOD_ICONS[m.icon] || CreditCard;
                          return (
                            <div key={i} className="flex items-center gap-1.5 bg-slate-100 px-3 py-1.5 rounded-full text-xs text-slate-700" data-testid={`pricing-method-pe-${i}`}>
                              <Icon className="w-3.5 h-3.5 text-[#1B84FF]" />
                              {m.label}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}
                  {(!display || (display.country !== "PE" && display.country !== "IN")) && p.payment_methods_intl?.length > 0 && (
                    <div data-testid="pricing-methods-intl">
                      <p className="text-[11px] font-semibold text-slate-600 mb-1.5">{p.payment_methods_intl_label || "Si estás fuera de Perú"}</p>
                      <div className="flex flex-wrap gap-2">
                        {p.payment_methods_intl.map((m, i) => {
                          const Icon = METHOD_ICONS[m.icon] || CreditCard;
                          return (
                            <div key={i} className="flex items-center gap-1.5 bg-slate-100 px-3 py-1.5 rounded-full text-xs text-slate-700" data-testid={`pricing-method-intl-${i}`}>
                              <Icon className="w-3.5 h-3.5 text-sky-600" />
                              {m.label}
                            </div>
                          );
                        })}
                      </div>
                      {p.mp_pen_note && (
                        <p className="text-[11px] text-slate-500 mt-2" data-testid="pricing-mp-pen-note">{p.mp_pen_note}</p>
                      )}
                    </div>
                  )}
                  </>
                  )}
                </div>
              )}

              <Link to="/register" className="block">
                <Button
                  className="h-auto w-full whitespace-normal rounded-full bg-[#1B84FF] py-6 text-base font-bold text-white shadow-[0_12px_32px_rgba(27,132,255,0.35)] transition-transform hover:-translate-y-0.5 hover:bg-[#0F6FDB]"
                  data-testid="pricing-buy-cta"
                >
                  {(p.cta || "Comprar tu QR ahora").split("—")[0].trim()}{priceLabelSemantic(planMonths) ? ` — ${priceLabelSemantic(planMonths)} ${planMonths === 12 ? t("pricing.per_12m") : t("pricing.per_6m")}` : ""}
                </Button>
              </Link>
              <p className="text-center text-sm text-slate-400 mt-3" data-testid="pricing-cta-note">
                {planMonths === 12 ? t("pricing.protection_12m") : t("pricing.protection_6m")}
              </p>
            </CardContent>
          </Card>
        </div>
      </div>
    </section>
  );
};

export default PricingCard;
